<template>
  <RevContainer class="pb-32 md:pb-56">
    <RevBreadcrumbs
      :ariaLabel="i18n(translations.breadcrumb)"
      :breads="breadcrumb"
      @click-breadcrumb="trackClickEvent('come_back_product_breadcrumb')"
    />

    <SeoText v-if="hasSeoText" class="mt-32 md:mb-56 md:mt-0" />

    <ReviewsBlock
      id="reviews"
      :ai-summary="null"
      :ai-summary-pending="false"
      :link="route.path"
      :main-title
      :next-reviews-cursor="reviewsResponse?.nextCursor"
      :pending-summary="ratingSummaryPending"
      :previous-reviews-cursor="reviewsResponse?.previousCursor"
      :product-id="String(route.params.uuid || '')"
      :product-title="mainTitle"
      :rating="ratesResponse"
      :rating-summary
      :reviews
      :reviews-pending="isInitialFetchPending"
    />

    <LinkBoosterList v-if="seoLinks" class="mt-56" :links="seoLinks">
      <template #title>
        <h3 class="heading-3" data-selector="tags-pp-title">
          {{ i18n(translations.seoLinksTitle) }}
        </h3>
      </template>
    </LinkBoosterList>
  </RevContainer>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevBreadcrumbs } from '@ds/components/Breadcrumbs'
import { RevContainer } from '@ds/components/Container'

import { TRACKING_PAGE_TYPE_BY_ROUTE_NAME } from '~/constants/trackingPageType'
import LinkBoosterList from '~/scopes/seo/components/LinkBoosterList.vue'

import { ROUTES } from '../../route-names'
import ReviewsBlock from '../components/ReviewsBlock/ReviewsBlock.vue'
import SeoText from '../components/SeoText/SeoText.vue'
import { useGlobalRating } from '../composables/useGlobalRating'
import { useRatingDetailsSummary } from '../composables/useRatingDetailsSummary'
import { useReviewsHead } from '../composables/useReviewsHead'
import { useReviewsList } from '../composables/useReviewsList'
import { useReviewsNavigation } from '../composables/useReviewsNavigation'
import { useReviewsTitle } from '../composables/useReviewsTitle'
import { useSeoLinks } from '../composables/useSeoLinks'
import { TRACKING_ZONE } from '../constants'
import translations from '../translations/reviews.translations'

const route = useRoute()
const i18n = useI18n()
const { trackClick } = useTracking()

const { mainTitle, shortTitle } = useReviewsTitle()
const { breadcrumb } = useReviewsNavigation(shortTitle)

const { ratesResponse } = useGlobalRating()
const { isInitialFetchPending, reviewsResponse } = useReviewsList()
const { data: ratingSummary, pending: ratingSummaryPending } =
  useRatingDetailsSummary()

const reviews = computed(() => {
  return reviewsResponse.value?.results
})

const { data: seoLinks } = useSeoLinks()

useReviewsHead(shortTitle, ratesResponse)

const hasSeoText = computed(() => route.name === ROUTES.PAGES.ALL)

const pageType = computed(() => {
  const routeName = route.name?.toString() || ''

  return TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName]
})

function trackClickEvent(name: string) {
  trackClick({
    zone: TRACKING_ZONE,
    pageType: pageType.value,
    name,
  })
}
</script>
